/**
 * RFC 7807 Problem Details for HTTP APIs
 * @see https://datatracker.ietf.org/doc/html/rfc7807
 */
export interface ProblemDetails {
  /** A URI reference that identifies the problem type */
  type: string;
  /** A short, human-readable summary of the problem type */
  title: string;
  /** The HTTP status code */
  status: number;
  /** A human-readable explanation specific to this occurrence */
  detail: string;
  /** A URI reference that identifies the specific occurrence */
  instance?: string;
  /** Timestamp of when the error occurred */
  timestamp?: string;
  /** For validation errors, contains field-specific errors */
  errors?: Record<string, string[]> | unknown;
  /** Whether the operation can be retried */
  retryable?: boolean;
  /** Exception type for internal errors */
  exceptionType?: string;
  /** Stack trace for internal errors (development only) */
  stackTrace?: string;
  /** Additional extension properties */
  [key: string]: unknown;
}

/**
 * Type guard to check if an object is a ProblemDetails response
 */
export function isProblemDetails(obj: unknown): obj is ProblemDetails {
  return (
    obj !== null &&
    typeof obj === 'object' &&
    'type' in obj &&
    typeof (obj as ProblemDetails).type === 'string' &&
    'title' in obj &&
    typeof (obj as ProblemDetails).title === 'string' &&
    'status' in obj &&
    typeof (obj as ProblemDetails).status === 'number'
  );
}

/**
 * Error class wrapping a ProblemDetails response.
 * Thrown by API communications when server returns an error.
 */
export class ApiError extends Error {
  public readonly problemDetails: ProblemDetails;
  public readonly response: Response | null;

  constructor(problemDetails: ProblemDetails, response?: Response | null) {
    super(problemDetails.detail || problemDetails.title);
    this.name = 'ApiError';
    this.problemDetails = problemDetails;
    this.response = response ?? null;
  }

  /** HTTP status code */
  get status(): number {
    return this.problemDetails.status;
  }

  /** Problem type URI */
  get type(): string {
    return this.problemDetails.type;
  }

  /** Check if this is an authentication error (401) */
  get isAuthentication(): boolean {
    return this.problemDetails.status === 401 || this.problemDetails.type.includes('unauthenticated');
  }

  /** Check if this is an authorization error (403) */
  get isAuthorization(): boolean {
    return this.problemDetails.status === 403 || this.problemDetails.type.includes('unauthorized');
  }

  /** Check if this is a validation error (422) */
  get isValidation(): boolean {
    return this.problemDetails.status === 422 || this.problemDetails.type.includes('validation-failure');
  }

  /** Check if this is a not found error (404) */
  get isNotFound(): boolean {
    return this.problemDetails.status === 404 || this.problemDetails.type.includes('not-found');
  }

  /** Check if this is a modify-not-found error */
  get isModifyNotFound(): boolean {
    return this.problemDetails.type.includes('modify-not-found');
  }

  /** Check if this error is retryable (transient) */
  get isRetryable(): boolean {
    return (
      this.problemDetails.retryable === true ||
      this.problemDetails.status === 503 ||
      this.problemDetails.type.includes('database-error')
    );
  }

  /** Get validation errors if this is a validation failure */
  get validationErrors(): unknown {
    return this.problemDetails.errors;
  }
}

/**
 * Problem type URIs matching server-side ProblemDetailsFactory.Types
 */
export const ProblemTypes = {
  NotFound: 'https://api.dealerwebmodule.com/errors/not-found',
  UnAuthenticated: 'https://api.dealerwebmodule.com/errors/unauthenticated',
  UnAuthorized: 'https://api.dealerwebmodule.com/errors/unauthorized',
  ValidationFailure: 'https://api.dealerwebmodule.com/errors/validation-failure',
  ModifyNotFound: 'https://api.dealerwebmodule.com/errors/modify-not-found',
  DatabaseError: 'https://api.dealerwebmodule.com/errors/database-error',
  InternalError: 'https://api.dealerwebmodule.com/errors/internal-error'
} as const;
